import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Clock,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Calendar,
} from 'lucide-react'

interface ScheduledPost {
  id: string
  title: string
  content: string
  platforms: string[]
  scheduledTime: string
  status: 'pending' | 'published' | 'failed'
}

const mockScheduledPosts: ScheduledPost[] = [
  {
    id: '1',
    title: '新功能发布通知',
    content: '我们很高兴地宣布 MultiPost 新版本发布...',
    platforms: ['微博', '知乎', 'Bilibili'],
    scheduledTime: '2026-07-02 10:00',
    status: 'pending',
  },
  {
    id: '2',
    title: '每周内容总结',
    content: '本周我们发布了多篇关于社交媒体营销的文章...',
    platforms: ['微信公众号', '头条号', '百家号'],
    scheduledTime: '2026-07-03 14:00',
    status: 'pending',
  },
  {
    id: '3',
    title: '产品更新日志',
    content: 'MultiPost v2.0 更新内容...',
    platforms: ['X (Twitter)', 'LinkedIn', 'Medium'],
    scheduledTime: '2026-07-01 09:00',
    status: 'published',
  },
  {
    id: '4',
    title: '用户反馈收集',
    content: '我们想听听您的意见...',
    platforms: ['小红书', '抖音'],
    scheduledTime: '2026-06-30 16:00',
    status: 'failed',
  },
]

export default function Schedule() {
  const [posts] = useState<ScheduledPost[]>(mockScheduledPosts)

  const getStatusIcon = (status: ScheduledPost['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />
      case 'published':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />
    }
  }

  const getStatusText = (status: ScheduledPost['status']) => {
    switch (status) {
      case 'pending':
        return '待发布'
      case 'published':
        return '已发布'
      case 'failed':
        return '发布失败'
    }
  }

  const getStatusColor = (status: ScheduledPost['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-500'
      case 'published':
        return 'bg-green-500/10 text-green-500'
      case 'failed':
        return 'bg-red-500/10 text-red-500'
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24 }}
      className="p-8"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            计划发布
            <span className="ml-3 rounded-full bg-foreground/10 px-3 py-1 text-xs font-medium">
              Beta
            </span>
          </h1>
          <p className="mt-2 text-muted-foreground">
            定时发布内容到多个社交媒体平台
          </p>
        </div>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Link
            to="/dashboard/publish"
            className="inline-flex items-center justify-center rounded-xl bg-foreground px-5 py-2.5 text-sm font-medium text-background shadow-lg shadow-foreground/20"
          >
            <Plus className="mr-2 h-4 w-4" />
            新建计划
          </Link>
        </motion.div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-xl border border-border/50 bg-card/50 p-5"
        >
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-yellow-500" />
            <span className="text-sm font-medium text-muted-foreground">待发布</span>
          </div>
          <p className="mt-2 text-3xl font-bold">
            {posts.filter(p => p.status === 'pending').length}
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-xl border border-border/50 bg-card/50 p-5"
        >
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-sm font-medium text-muted-foreground">已发布</span>
          </div>
          <p className="mt-2 text-3xl font-bold">
            {posts.filter(p => p.status === 'published').length}
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="rounded-xl border border-border/50 bg-card/50 p-5"
        >
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm font-medium text-muted-foreground">发布失败</span>
          </div>
          <p className="mt-2 text-3xl font-bold">
            {posts.filter(p => p.status === 'failed').length}
          </p>
        </motion.div>
      </div>

      {/* Scheduled Posts List */}
      <div>
        <h2 className="text-lg font-semibold mb-4">计划列表</h2>
        <div className="space-y-4">
          {posts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              whileHover={{ y: -2 }}
              className="rounded-xl border border-border/50 bg-card/50 p-6 hover:border-border hover:bg-card transition-all duration-200"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold">{post.title}</h3>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(post.status)}`}>
                      {getStatusIcon(post.status)}
                      {getStatusText(post.status)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                    {post.content}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      {post.scheduledTime}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {post.platforms.map((platform) => (
                        <span
                          key={platform}
                          className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium"
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="rounded-lg p-2 hover:bg-secondary transition-colors"
                  >
                    <Edit className="h-4 w-4 text-muted-foreground" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="rounded-lg p-2 hover:bg-secondary transition-colors"
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}
